// The legacy DashboardDrawer: the dialog in which an announcement is planned,
// edited, or saved as a favourite — and the OverlapDialog it opens when the
// new one clashes with one already planned.
//
// It is opened with a request instead of through a ref:
//
//   new        "Neue Ansage", optionally with the type already chosen (the
//              phone's wizard chooses it first)
//   edit       the pencil on a planned announcement
//   favourite  a favourite tile: prefilled from the tile, or empty for a new
//              favourite
//
// Editing is what it was in the legacy application: the old announcement is
// withdrawn and the new one planned in its place — in one request now, so that
// a refused plan leaves the old announcement standing.
//
// Every date and time here is Vienna's wall clock, whatever zone the phone is
// in; see components/clock/dates.

import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  AppBar,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card as MuiCard,
  CardActionArea,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseIcon from "@mui/icons-material/Close";
import EastIcon from "@mui/icons-material/East";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import LoopIcon from "@mui/icons-material/Loop";
import { lighten } from "polished";
import { useLocation, useNavigate } from "react-router-dom";
import {
  api,
  ApiError,
  type Card,
  type FavouriteCard,
  type FormDescriptor,
  type PreviewResult,
} from "~/api";
import { useBootstrap, useRefresh } from "~/bootstrap";
import { useLang, useT } from "~/i18n";
import { DefaultPrimary } from "~/theme";
import { Clock, defaultWindow, modeOf } from "~/components/clock/clock";
import { addMinutes, floorQuarter, format, parse, fmtTime, startOfDay, viennaNow } from "~/components/clock/dates";
import { allowedRepeats, initialValue, type FormValue } from "~/components/form";
import { missingFields } from "~/components/required";
import { announcementText } from "~/components/announcement";
import { isOffice, modeFor, repeatLabel, titleKey } from "~/legacy/helpers";
import AcceptIcon from "~/legacy/icons/AcceptIcon";
import PlayIcon from "~/legacy/icons/PlayIcon";
import { SoundDialog, } from "./sound";

export type DrawerRequest =
  | { kind: "new"; messageId?: number; favourite?: boolean }
  | { kind: "edit"; card: Card }
  | { kind: "favourite"; slot: number; favourite?: FavouriteCard };

/** A stand-in for the descriptor while no type is chosen: the legacy default type was a date and time. */
const placeholder: FormDescriptor = {
  companyMessageId: 0,
  name: "",
  icon: "datetime",
  summaryTitleKey: "datetime-announcement",
  kind: "absence",
  fields: ["start", "end"],
  start: { required: true, granularity: "minute", labelKey: "startdate" },
  end: { required: true, granularity: "minute", labelKey: "enddate" },
  preview: false,
  canPlan: false,
};

function emptyValue(): FormValue {
  const w = defaultWindow("datetime");
  return {
    companyMessageId: 0,
    startsAt: format(w.start),
    endsAt: w.end ? format(w.end) : null,
    proxyUserId: 0,
    displayName: "",
    notify: "none",
    repeat: "none",
  };
}

/** The legacy drawer preselected the only stand-in there was. */
function withOnlyProxy(d: FormDescriptor, v: FormValue): FormValue {
  if (!v.proxyUserId && d.proxy?.options.length === 1) return { ...v, proxyUserId: d.proxy.options[0]!.id };
  return v;
}

/**
 * local turns what the server sends (RFC 3339) into what the form holds:
 * Vienna's wall clock at that instant, not the phone's.
 */
function local(s: string | undefined): string | null {
  const d = parse(s);
  return d ? format(d) : null;
}

/**
 * unchanged sends back the server's own timestamp for a side of the window
 * the person did not touch, so that saving an edit unchanged keeps the very
 * same instant — even in the hour the clocks go back, which a wall-clock time
 * alone cannot name.
 */
function unchanged(v: FormValue, card: Card): FormValue {
  return {
    ...v,
    startsAt: card.startsAt && v.startsAt === local(card.startsAt) ? card.startsAt : v.startsAt,
    endsAt: card.endsAt && v.endsAt === local(card.endsAt) ? card.endsAt : v.endsAt,
  };
}

export function DashboardDrawer({ request, onClose }: { request: DrawerRequest | null; onClose: () => void }) {
  const theme = useTheme();
  const belowsm = useMediaQuery(theme.breakpoints.down("sm"));
  const t = useT();
  const b = useBootstrap();
  const refresh = useRefresh();
  const location = useLocation();
  const navigate = useNavigate();

  const messages = b.messages;
  const filled = b.favourites.filter((f) => !f.empty);
  const firstEmptySlot = useMemo(() => {
    for (let i = 0; i < b.limits.favourites; i++) {
      if (!b.favourites.some((f) => f.slot === i && !f.empty)) return i;
    }
    return -1;
  }, [b.favourites, b.limits.favourites]);

  const [open, setOpen] = useState(false);
  const [messageId, setMessageId] = useState<number | "">("");
  const [value, setValue] = useState<FormValue>(emptyValue);
  const [messageError, setMessageError] = useState(false);
  const [representativeError, setRepresentativeError] = useState(false);

  const [isEdit, setIsEdit] = useState<Card | null>(null);
  const [isQuickStart, setIsQuickStart] = useState(false);
  const [newQuickStart, setNewQuickStart] = useState(false);
  const [slot, setSlot] = useState(-1);
  const [updated, setUpdated] = useState(false);

  const [loading, setLoading] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [quickStartLoading, setQuickStartLoading] = useState(false);
  const [error, setError] = useState("");
  const [sound, setSound] = useState<PreviewResult | null>(null);

  const [overlaps, setOverlaps] = useState<Card[]>([]);
  const [overlapEntry, setOverlapEntry] = useState<FormValue | null>(null);
  // The announcement being edited, which the new one replaces however the
  // clash is settled.
  const [overlapReplaces, setOverlapReplaces] = useState<number[]>([]);

  const titleRef = useRef("MbM");
  const openedAt = useRef(0);

  const descriptor = messageId === "" ? undefined : messages.find((m) => m.companyMessageId === messageId);
  const shown = descriptor ?? placeholder;
  const mode = descriptor ? modeOf(descriptor) : "datetime";

  // --- opening ----------------------------------------------------------------------

  useEffect(() => {
    if (!request) {
      setOpen(false);
      return;
    }
    titleRef.current = document.title;
    openedAt.current = Date.now();
    setError("");
    setMessageError(false);
    setRepresentativeError(false);
    setUpdated(false);
    setIsEdit(null);
    setIsQuickStart(false);
    setNewQuickStart(false);
    setSlot(-1);

    if (request.kind === "new") {
      const d = request.messageId ? messages.find((m) => m.companyMessageId === request.messageId) : undefined;
      const asFavourite = !!request.favourite && filled.length < b.limits.favourites;
      setNewQuickStart(asFavourite);
      setSlot(asFavourite ? firstEmptySlot : -1);
      document.title = t(asFavourite ? "vmm-new-favourite" : "vmm-new-abscense");
      setMessageId(d ? d.companyMessageId : "");
      setValue(d ? withOnlyProxy(d, initialValue(d)) : emptyValue());
    } else if (request.kind === "edit") {
      const c = request.card;
      setIsEdit(c);
      document.title = t("vmm-edit-voicemail");
      setMessageId(c.companyMessageId);
      setValue({
        companyMessageId: c.companyMessageId,
        startsAt: local(c.startsAt),
        endsAt: local(c.endsAt),
        proxyUserId: c.proxy?.id ?? 0,
        displayName: c.displayName ?? "",
        notify: c.notify || "none",
        repeat: c.repeat || "none",
      });
    } else {
      setSlot(request.slot);
      const f = request.favourite;
      if (!f || f.empty) {
        setNewQuickStart(true);
        document.title = t("vmm-new-favourite");
        setMessageId("");
        setValue(emptyValue());
      } else {
        setIsQuickStart(true);
        document.title = t("vmm-new-abscense");
        const d = messages.find((m) => m.companyMessageId === f.companyMessageId);
        // The window starts in the quarter hour that is running and lasts as
        // long as the favourite says — the legacy handleQuickStart.
        // A date announcement starts at midnight, as a new one does.
        const now = viennaNow();
        const start = d && modeOf(d) === "date" ? startOfDay(now) : floorQuarter(now);
        const end = f.openEnded || !f.durationMinutes ? null : addMinutes(start, f.durationMinutes);
        setMessageId(d ? d.companyMessageId : "");
        setValue({
          ...(d ? initialValue(d) : emptyValue()),
          companyMessageId: f.companyMessageId ?? 0,
          startsAt: format(start),
          endsAt: d && modeOf(d) === "open" ? null : end ? format(end) : d ? initialValue(d).endsAt : null,
          proxyUserId: f.proxy?.id ?? 0,
          displayName: f.displayName ?? "",
          notify: f.notify || d?.notify?.default || "none",
        });
      }
    }
    setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  // Leaving the phone's wizard goes back to the start page — but not while a
  // clash is still to be settled, or the dialog asking about it would go with
  // the wizard.
  const close = (away = true) => {
    setOpen(false);
    document.title = titleRef.current;
    onClose();
    if (away && location.pathname === "/neu") navigate("/");
  };

  // --- the rules ----------------------------------------------------------------------

  const repeats = useMemo(
    () => allowedRepeats(shown.repeat?.options ?? [], value.startsAt, value.endsAt),
    [shown.repeat, value.startsAt, value.endsAt],
  );

  // A repeat that the window no longer allows becomes "never" — the legacy
  // isSelectedRecurringValueAllowed effect.
  useEffect(() => {
    if (value.repeat !== "none" && !repeats.some((r) => r.value === value.repeat)) {
      setValue((v) => ({ ...v, repeat: "none" }));
    }
  }, [repeats, value.repeat]);

  const disableButton = () => {
    if (messageId === "" || !descriptor) return true;
    const start = parse(value.startsAt);
    const end = parse(value.endsAt);
    if (end && start && end < start) return true;
    if (missingFields(descriptor, value).length > 0) return true;
    // A start more than a day in the past.
    if (start && viennaNow().getTime() - start.getTime() > 23 * 3_600_000 + 59 * 60_000) return true;
    return false;
  };
  const disabled = disableButton();

  // --- choosing a type -------------------------------------------------------------

  const check = (id: number) => {
    const d = messages.find((m) => m.companyMessageId === id);
    if (!d) return;
    const next = withOnlyProxy(d, initialValue(d));
    setValue({ ...next, displayName: value.displayName });
    setMessageId(id);
    setMessageError(false);
    setRepresentativeError(false);
    if (isQuickStart) setUpdated(true);
  };

  // --- the three buttons -------------------------------------------------------------

  const body = (v: FormValue) => ({ ...v, companyMessageId: messageId === "" ? 0 : messageId });

  const plan = async (v: FormValue) => {
    await api<Card>("/api/portal/announcements", { method: "POST", body: body(v) });
  };

  const fail = (e: unknown, v: FormValue, replaces: number[] = []) => {
    if (e instanceof ApiError && e.conflicts?.length) {
      setOverlapEntry(body(v));
      setOverlapReplaces(replaces);
      setOverlaps(e.conflicts);
      return;
    }
    setError(e instanceof ApiError ? e.detail : t("something-went-wrong"));
  };

  const createEntry = async () => {
    setLoading(true);
    setError("");
    try {
      await plan(value);
      refresh();
      close();
    } catch (e) {
      fail(e, value);
      if (e instanceof ApiError && e.conflicts?.length) close(false);
    } finally {
      setLoading(false);
    }
  };

  // One request withdraws the old announcement and plans the new one; the
  // server does both or neither, so a refusal leaves the old one standing.
  const editEntry = async () => {
    if (!isEdit) return;
    const v = unchanged(value, isEdit);
    setLoading(true);
    setError("");
    try {
      await api<Card>("/api/portal/announcements/replace", {
        method: "POST",
        body: { ...body(v), replaces: [isEdit.id] },
      });
      refresh();
      close();
    } catch (e) {
      refresh();
      fail(e, v, [isEdit.id]);
      if (e instanceof ApiError && e.conflicts?.length) close(false);
    } finally {
      setLoading(false);
    }
  };

  const updateQuickStart = async () => {
    const target = isQuickStart || newQuickStart ? slot : firstEmptySlot;
    if (target < 0 || messageId === "") return;
    const start = parse(value.startsAt);
    const end = parse(value.endsAt);
    setQuickStartLoading(true);
    setError("");
    try {
      await api(`/api/portal/favourites/${target}`, {
        method: "PUT",
        body: {
          companyMessageId: messageId,
          proxyUserId: value.proxyUserId,
          durationMinutes: start && end ? Math.max(Math.round((end.getTime() - start.getTime()) / 60_000), 0) : 0,
          displayName: isQuickStart && request?.kind === "favourite" ? (request.favourite?.displayName ?? "") : "",
          notify: value.notify,
        },
      });
      refresh();
      close();
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : t("something-went-wrong"));
    } finally {
      setQuickStartLoading(false);
    }
  };

  const fetchSound = async () => {
    if (messageId === "") return;
    setAudioLoading(true);
    setError("");
    try {
      const p = await api<PreviewResult>("/api/portal/announcements/preview", {
        method: "POST",
        body: { companyMessageId: messageId, endsAt: value.endsAt, proxyUserId: value.proxyUserId },
      });
      setSound(p);
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : t("something-went-wrong"));
    } finally {
      setAudioLoading(false);
    }
  };

  // --- the menu of types ------------------------------------------------------------

  // The legacy checkForConsistency: when no two types share a combination,
  // a type can be named by its kind and its stand-in alone.
  const consistent = useMemo(() => {
    const seen = new Set<string>();
    for (const m of messages) {
      const key = `${modeOf(m)}-${m.proxy?.source ?? "none"}`;
      if (seen.has(key)) return false;
      seen.add(key);
    }
    return true;
  }, [messages]);

  const soft = theme.palette.mode === "light" && theme.palette.primary.main === DefaultPrimary;

  const proxyKind = descriptor?.proxy ? (isOffice(descriptor) ? "office" : "person") : "none";
  const proxyOptions = descriptor?.proxy?.options ?? [];
  const chosenProxy = proxyOptions.find((o) => o.id === value.proxyUserId);
  const proxyLabel =
    proxyKind === "person" ? t("representation") : proxyKind === "office" ? t("office-representation") : t("no-representation");
  const trim46 = (s: string) => (s.length > 46 ? `${s.substring(0, 46)}...` : s);

  const text = descriptor ? announcementText(descriptor.body) || t("no-announcement-text") : t("no-announcement-text");

  const showFavouriteButton =
    (isQuickStart && !newQuickStart) || (filled.length < b.limits.favourites && !newQuickStart && !isEdit);

  return (
    <Fragment>
      <Dialog
        open={open}
        maxWidth="xs"
        fullScreen={belowsm}
        onClose={() => close()}
        sx={{
          "& .MuiDialog-paper": {
            backgroundImage: "none",
            borderRadius: belowsm ? 0 : "16px",
            "::-webkit-scrollbar": { display: "none" },
          },
        }}
      >
        <AppBar
          position="sticky"
          color="inherit"
          sx={{ width: "inherit", boxShadow: "none", bgcolor: "background.paper", backgroundImage: "none" }}
        >
          <Grid container sx={{ p: 2, pb: belowsm ? 1.5 : 1, pr: 1, borderBottom: 0, borderColor: "divider" }}>
            <Typography
              noWrap
              sx={{ color: "text.primary", fontSize: 32, fontWeight: 700, letterSpacing: "-0.8px", lineHeight: "normal", width: "85%" }}
            >
              {isEdit ? t("edit-voicemail") : newQuickStart ? t("new-favorite") : t("new-voicemail")}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title={t("close")} placement="right">
              <IconButton onClick={() => close()} sx={{ py: 0, px: 0.5, color: "text.primary", "&:hover": { color: "error.main" } }}>
                <CloseIcon sx={{ color: "inherit", fontSize: 30 }} />
              </IconButton>
            </Tooltip>
          </Grid>
        </AppBar>

        <Box sx={{ px: 2 }}>
          <TextField
            variant="outlined"
            id="Message"
            label={t("message-type")}
            autoFocus={messageId === ""}
            fullWidth
            select
            value={messageId}
            onChange={(e) => check(Number(e.target.value))}
            onBlur={() => {
              // Not for the blur the opening itself causes, when the dialog
              // takes the focus it was just given.
              if (messageId === "" && Date.now() - openedAt.current > 400) setMessageError(true);
            }}
            SelectProps={{
              MenuProps: {
                sx: { "& .MuiMenu-paper": { bgcolor: "background.default", backgroundImage: "none" } },
              },
            }}
            error={messageError}
            sx={{ mt: 1 }}
          >
            {messages.map((m) => {
              const sub = m.subtitle?.trim();
              const office = m.proxy?.source === "secretariats";
              const person = m.proxy?.source === "users";
              return (
                <MenuItem key={m.companyMessageId} value={m.companyMessageId} disabled={!m.canPlan} style={{ whiteSpace: "normal" }}>
                  <Grid container direction="column">
                    <Typography noWrap sx={{ color: "text.primary", fontSize: "16px", fontWeight: 400, lineHeight: "normal" }}>
                      {sub ? m.name : consistent ? t(titleKey(modeOf(m))) : m.name}
                    </Typography>
                    <Grid container columnGap={0.5}>
                      {sub ? (
                        <Typography
                          noWrap
                          sx={{ color: soft ? "#4F4F4F" : "text.secondary", fontSize: "12px", fontWeight: 400, lineHeight: "normal" }}
                        >
                          {m.subtitle}
                        </Typography>
                      ) : (
                        <>
                          <Typography
                            noWrap
                            sx={{ color: soft ? "#4F4F4F" : "text.primary", fontSize: "12px", fontWeight: 700, lineHeight: "normal" }}
                          >
                            {office ? t("office") : person ? t("with") : t("without")}
                          </Typography>
                          <Typography
                            noWrap
                            sx={{ color: soft ? "#4F4F4F" : "text.secondary", fontSize: "12px", fontWeight: 400, lineHeight: "normal" }}
                          >
                            {t("representation")}
                          </Typography>
                        </>
                      )}
                    </Grid>
                  </Grid>
                </MenuItem>
              );
            })}
          </TextField>
        </Box>

        <Box sx={{ px: 2, pt: belowsm ? 2 : 3, display: isQuickStart || newQuickStart ? "none" : "block" }}>
          <TextField
            variant="outlined"
            label={t("title")}
            value={value.displayName}
            onChange={(e) => setValue({ ...value, displayName: e.target.value })}
            inputProps={{ maxLength: descriptor?.displayName?.maxLength ?? 120 }}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        <Box sx={{ pr: 1, pt: belowsm ? 1 : 2, pb: belowsm ? 1 : 2 }}>
          <Clock
            descriptor={shown}
            startsAt={value.startsAt}
            endsAt={value.endsAt}
            onChange={(startsAt, endsAt) => {
              setValue((v) => ({ ...v, startsAt, endsAt }));
              if (isQuickStart) setUpdated(true);
            }}
          />
        </Box>

        <Box sx={{ px: 2, pb: proxyKind === "none" ? 1 : 0 }}>
          <TextField
            variant="outlined"
            select
            label={t("repetition")}
            fullWidth
            value={value.repeat}
            onChange={(e) => setValue({ ...value, repeat: e.target.value })}
            InputLabelProps={{ shrink: true }}
            SelectProps={{
              MenuProps: {
                sx: { "& .MuiMenu-paper": { bgcolor: "background.default", backgroundImage: "none" } },
              },
            }}
            sx={{ "& .MuiSvgIcon-root.Mui-disabled": { color: "background.default" } }}
          >
            {(shown.repeat?.options.length ? shown.repeat.options : [{ value: "none" }]).map((o) => (
              <MenuItem key={o.value} value={o.value} disabled={o.value !== "none" && !repeats.some((r) => r.value === o.value)}>
                <Typography>{t(repeatLabel[o.value] ?? `repeat-${o.value}`)}</Typography>
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {/* New against the legacy drawer: a company may let a person choose
            who is notified for this one announcement. Drawn like the fields
            around it, and only where the company allows it. */}
        {descriptor?.notify?.show ? (
          <Box sx={{ px: 2, pt: 2 }}>
            <TextField
              variant="outlined"
              select
              label={t("notification-type")}
              fullWidth
              value={value.notify}
              onChange={(e) => setValue({ ...value, notify: e.target.value })}
              InputLabelProps={{ shrink: true }}
              SelectProps={{
                MenuProps: {
                  sx: { "& .MuiMenu-paper": { bgcolor: "background.default", backgroundImage: "none" } },
                },
              }}
            >
              {descriptor.notify.options.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  <Typography>{t(o.labelKey)}</Typography>
                </MenuItem>
              ))}
            </TextField>
          </Box>
        ) : null}

        <Box
          sx={{
            pb: 1,
            pt: proxyKind === "none" ? 1 : 2,
            pl: 2,
            pr: 1,
            display: proxyKind === "none" && belowsm ? "none" : "inline",
          }}
        >
          {proxyOptions.length < 20 ? (
            <TextField
              variant="outlined"
              id="representation"
              label={proxyLabel}
              fullWidth
              select
              value={proxyOptions.some((o) => o.id === value.proxyUserId) ? value.proxyUserId : ""}
              onChange={(e) => {
                setValue({ ...value, proxyUserId: Number(e.target.value) });
                setRepresentativeError(false);
                setUpdated(true);
              }}
              onBlur={() => {
                if (!chosenProxy) setRepresentativeError(true);
              }}
              error={representativeError || (!!chosenProxy && !chosenProxy.canBeNamed)}
              helperText={chosenProxy && !chosenProxy.canBeNamed ? t("proxy-no-recording") : undefined}
              disabled={proxyKind === "none"}
              SelectProps={{
                MenuProps: {
                  sx: { "& .MuiMenu-paper": { bgcolor: "background.default", backgroundImage: "none" } },
                },
              }}
              sx={{ pr: 1, "& .MuiSvgIcon-root.Mui-disabled": { color: "background.default" } }}
            >
              {proxyOptions.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  <Typography>{trim46(o.name)}</Typography>
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <Autocomplete
              id="representation"
              options={proxyOptions}
              getOptionLabel={(o) => o.name}
              value={chosenProxy ?? null}
              onChange={(_, o) => {
                setValue({ ...value, proxyUserId: o ? o.id : 0 });
                setRepresentativeError(false);
                setUpdated(true);
              }}
              onBlur={() => {
                if (!chosenProxy) setRepresentativeError(true);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={proxyLabel}
                  variant="outlined"
                  fullWidth
                  error={representativeError || (!!chosenProxy && !chosenProxy.canBeNamed)}
                  helperText={chosenProxy && !chosenProxy.canBeNamed ? t("proxy-no-recording") : undefined}
                />
              )}
              sx={{ pr: 1 }}
              disabled={proxyKind === "none"}
              filterOptions={(options, state) => {
                const q = state.inputValue.trim().toLowerCase();
                return (q ? options.filter((o) => o.name.toLowerCase().includes(q)) : options).slice(0, 500);
              }}
            />
          )}
        </Box>

        <Box sx={{ pr: 2, ml: 2, mt: 1 }}>
          <Box sx={{ borderRadius: "10px", bgcolor: "secondary.main", px: 2, py: 2.5 }}>
            <Typography sx={{ color: "text.primary", fontSize: "14px", fontWeight: 700, lineHeight: "16px", letterSpacing: "-0.4px", pb: 0.5 }}>
              {t("announcement-text")}
            </Typography>
            <Typography
              sx={{ color: "text.primary", fontSize: "14px", fontWeight: 400, lineHeight: "16px", letterSpacing: "-0.4px", pr: 3, whiteSpace: "pre-line" }}
            >
              {text}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
          <Grid container sx={{ mt: 2, mb: 1, px: 2 }}>
            {error ? (
              <Grid item xs={12}>
                <Typography role="alert" sx={{ color: "error.main", fontSize: 14, mb: 1 }}>
                  {error}
                </Typography>
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <Box
                onClick={() => {
                  if (messageId === "" && open) setMessageError(true);
                  if (descriptor?.proxy?.required && !chosenProxy && open) setRepresentativeError(true);
                }}
              >
                <LoadingButton
                  variant="contained"
                  color="primary"
                  loading={loading || (newQuickStart && quickStartLoading)}
                  fullWidth
                  onClick={newQuickStart ? updateQuickStart : isEdit ? editEntry : createEntry}
                  startIcon={
                    loading ? null : (
                      <Avatar sx={{ bgcolor: "primary.contrastText", ml: 0.15, height: 43, width: 43 }}>
                        {disabled ? <AcceptIcon color={theme.palette.info.main} /> : <AcceptIcon />}
                      </Avatar>
                    )
                  }
                  loadingIndicator={<CircularProgress color="inherit" size={26} />}
                  sx={{
                    textTransform: "none",
                    height: 52,
                    justifyContent: "flex-start",
                    pl: 1,
                    borderRadius: "26px",
                    "&:hover": { bgcolor: lighten(0.05, theme.palette.primary.main) },
                    "&:disabled": { backgroundColor: "action.disableBackground", borderColor: "action.hover" },
                  }}
                  disableElevation
                  disabled={disabled}
                >
                  <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                    <Typography
                      sx={{
                        color: disabled ? "text.primary" : "primary.contrastText",
                        fontSize: 22,
                        visibility: loading ? "hidden" : "visible",
                        display: open ? "inline" : "none",
                        fontWeight: 700,
                        letterSpacing: "-0.55px",
                      }}
                    >
                      {newQuickStart ? t("save-favorite") : t("save-entry")}
                    </Typography>
                  </Box>
                </LoadingButton>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <LoadingButton
                variant="outlined"
                color="primary"
                fullWidth
                onClick={fetchSound}
                loading={audioLoading}
                startIcon={
                  audioLoading ? null : (
                    <Avatar
                      sx={{
                        bgcolor: "transparent",
                        border: disabled ? 1 : 2,
                        borderColor: disabled ? "info.main" : "primary.main",
                        height: 43,
                        width: 43,
                        ml: -0.2,
                      }}
                    >
                      {disabled ? <PlayIcon fontSize={20} color={theme.palette.info.main} /> : <PlayIcon fontSize={20} />}
                    </Avatar>
                  )
                }
                loadingIndicator={<CircularProgress color="inherit" size={26} />}
                sx={{
                  textTransform: "none",
                  height: 53,
                  border: 2,
                  my: belowsm ? 1 : 2,
                  pl: 1,
                  justifyContent: "flex-start",
                  borderRadius: "26px",
                  "&:hover": { border: 2 },
                }}
                disabled={disabled || !descriptor?.preview}
              >
                <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                  <Typography
                    sx={{
                      color: disabled ? "text.primary" : "primary.main",
                      fontSize: 22,
                      visibility: audioLoading ? "hidden" : "visible",
                      fontWeight: 700,
                      letterSpacing: "-0.55px",
                    }}
                  >
                    {t("test-listen")}
                  </Typography>
                </Box>
              </LoadingButton>
            </Grid>
            <Grid item xs={12} sx={{ display: showFavouriteButton ? "inherit" : "none", pb: 2 }}>
              <LoadingButton
                variant="outlined"
                color={theme.palette.mode === "light" ? "favorite" : "info"}
                onClick={updateQuickStart}
                fullWidth
                startIcon={
                  quickStartLoading ? null : (
                    <Avatar
                      sx={{
                        bgcolor: updated ? "text.primary" : theme.palette.mode === "light" ? "favorite.main" : "info.main",
                        height: 43,
                        width: 43,
                        ml: -0.2,
                      }}
                    >
                      <LoopIcon sx={{ color: "background.default", transform: "rotate(-45deg)" }} />
                    </Avatar>
                  )
                }
                loading={quickStartLoading}
                loadingIndicator={<CircularProgress color="inherit" size={26} />}
                sx={{
                  textTransform: "none",
                  height: 53,
                  border: 2,
                  pl: 1,
                  justifyContent: "flex-start",
                  borderRadius: "26px",
                  borderColor: updated ? "text.primary" : "inherit",
                  "&:hover": { border: 2, borderColor: updated ? "text.primary" : "inherit" },
                  "&.Mui-selected": { border: 2, borderColor: updated ? "text.primary" : "inherit" },
                }}
                disabled={disabled}
              >
                <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                  <Typography
                    sx={{
                      color: updated ? "text.primary" : theme.palette.mode === "light" ? "favorite.main" : "info.main",
                      fontSize: 22,
                      visibility: loading || quickStartLoading ? "hidden" : "visible",
                      display: open ? "inline" : "none",
                      fontWeight: 700,
                      letterSpacing: "-0.55px",
                    }}
                  >
                    {isQuickStart ? t("update-favorite") : t("save-as-favorite")}
                  </Typography>
                </Box>
              </LoadingButton>
            </Grid>
          </Grid>
        </Box>
      </Dialog>

      <SoundDialog
        url={sound?.url ?? null}
        message={t(titleKey(mode))}
        startsAt={value.startsAt}
        endsAt={value.endsAt}
        noEnd={mode === "open"}
        onClose={() => setSound(null)}
      />

      <OverlapDialog
        entry={overlapEntry}
        overlaps={overlaps}
        replaces={overlapReplaces}
        onClose={() => {
          setOverlaps([]);
          setOverlapEntry(null);
          setOverlapReplaces([]);
          if (location.pathname === "/neu") navigate("/");
        }}
      />
    </Fragment>
  );
}

// --- the clash --------------------------------------------------------------------------

const monthsShort = {
  de: ["Jan.", "Feb.", "März", "Apr.", "Mai", "Juni", "Juli", "Aug.", "Sep.", "Okt.", "Nov.", "Dez."],
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
};

type Entry = { id: number; companyMessageId: number; icon?: string; startsAt: string | null; endsAt: string | null; proxyName?: string };

/**
 * The legacy OverlapDialog: the new announcement on the left, the ones it
 * clashes with on the right (with arrows when there are several), and a
 * choice between them. Choosing the new one withdraws the old ones and plans
 * it — in one request, which the server carries out entirely or not at all;
 * choosing an old one keeps everything as it was.
 *
 * `replaces` is the announcement being edited, if any: it goes whichever way
 * the clash is settled in favour of the new one.
 */
function OverlapDialog({
  entry,
  overlaps,
  replaces,
  onClose,
}: {
  entry: FormValue | null;
  overlaps: Card[];
  replaces: number[];
  onClose: () => void;
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const t = useT();
  const lang = useLang();
  const b = useBootstrap();
  const refresh = useRefresh();

  const [selected, setSelected] = useState<number | undefined>();
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setSelected(undefined);
    setIndex(0);
    setBusy(false);
    setError("");
  }, [entry, overlaps]);

  const open = !!entry && overlaps.length > 0;
  const fromList = overlaps.filter((e) => e.id === selected);

  const when = (s: string | null) => {
    const d = parse(s);
    if (!d) return "";
    return `${String(d.getDate()).padStart(2, "0")}. ${monthsShort[lang][d.getMonth()]} ${fmtTime(d)}`;
  };

  const asEntry = (c: Card): Entry => ({
    id: c.id,
    companyMessageId: c.companyMessageId,
    icon: c.icon,
    startsAt: c.startsAt,
    endsAt: c.endsAt ?? null,
    proxyName: c.proxy?.name,
  });

  const newEntry: Entry | undefined = entry
    ? {
        id: 0,
        companyMessageId: entry.companyMessageId,
        startsAt: entry.startsAt,
        endsAt: entry.endsAt,
        proxyName: b.messages
          .find((m) => m.companyMessageId === entry.companyMessageId)
          ?.proxy?.options.find((o) => o.id === entry.proxyUserId)?.name,
      }
    : undefined;

  // Keeping the old ones closes the dialog. Choosing the new one closes it
  // only once the server has done it; a refusal stays in the dialog, and
  // nothing has changed, so it can be tried again or settled the other way.
  const manageClose = async () => {
    if (fromList.length > 0 || !entry) {
      onClose();
      return;
    }
    setBusy(true);
    setError("");
    try {
      const ids = [...new Set([...replaces, ...overlaps.map((o) => o.id)])];
      await api<Card>("/api/portal/announcements/replace", { method: "POST", body: { ...entry, replaces: ids } });
      refresh();
      onClose();
    } catch (e) {
      refresh();
      setError(e instanceof ApiError ? e.detail : t("something-went-wrong"));
    } finally {
      setBusy(false);
    }
  };

  const CardItem = ({ obj }: { obj: Entry | undefined }) => {
    if (!obj) return <div />;
    const off = selected !== obj.id && selected !== undefined;
    const ink = off ? "text.disabled" : "primary.contrastText";
    const line = off || theme.palette.mode === "light" ? "divider" : "primary.main";
    const d = b.messages.find((m) => m.companyMessageId === obj.companyMessageId);
    return (
      <MuiCard
        sx={{
          bgcolor: off ? (theme.palette.mode === "dark" ? "action.disabledBackground" : "action.selected") : "primary.dark",
          border: theme.palette.mode === "dark" ? 1.5 : 0,
          borderColor: off ? "divider" : theme.palette.mode === "dark" ? "primary.main" : "primary.contrastText",
          width: 230,
          height: 205,
          borderRadius: 2,
          boxShadow: "none",
        }}
      >
        <CardActionArea onClick={() => setSelected(obj.id)} sx={{ width: "100%", height: "100%" }}>
          <CardContent>
            <Box>
              <Grid container sx={{ borderBottom: 2, borderColor: line }}>
                <Typography noWrap sx={{ my: 0.5, color: ink, fontWeight: "bold" }}>
                  {t(titleKey(modeFor(d, obj.icon)))}
                </Typography>
              </Grid>
              <Grid container sx={{ py: 3, borderBottom: 2, borderColor: line }}>
                <Grid item xs={4} sx={{ textAlign: "center" }}>
                  <Typography sx={{ color: ink, fontSize: 15 }}>{when(obj.startsAt)}</Typography>
                </Grid>
                <Grid item xs={4} sx={{ color: ink, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <EastIcon />
                </Grid>
                <Grid item xs={4} sx={{ textAlign: "center" }}>
                  <Typography sx={{ color: ink, fontSize: 15, pr: obj.endsAt ? 0 : 1 }}>
                    {obj.endsAt ? when(obj.endsAt) : t("no-enddate")}
                  </Typography>
                </Grid>
              </Grid>
              {obj.proxyName ? (
                <Fragment>
                  <Typography sx={{ color: ink, fontSize: 12, mt: 0.5 }}>{t("representation")}</Typography>
                  <Typography noWrap sx={{ color: ink, fontSize: 14 }}>
                    {obj.proxyName}
                  </Typography>
                </Fragment>
              ) : (
                <Fragment>
                  <Typography sx={{ color: ink, fontSize: 12, mt: 0.5 }}>{t("no-representation")}</Typography>
                  <Box sx={{ width: "100%", height: 21 }} />
                </Fragment>
              )}
            </Box>
          </CardContent>
        </CardActionArea>
      </MuiCard>
    );
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDialog-paper": {
          bgcolor: "background.default",
          backgroundImage: "none",
          borderRadius: fullScreen ? 0 : 6,
        },
      }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "center", mt: fullScreen ? 5 : 0 }}>
        <Typography>
          {selected === undefined
            ? overlaps.length > 1
              ? t("x-overlapping-entries", { x: overlaps.length })
              : t("one-overlapping-entry")
            : fromList.length > 0
              ? fromList.length === 1
                ? t("keep-exisiting-entry")
                : t("keep-existing-entries")
              : t("activate-new-entry")}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pb: 0, mt: fullScreen ? 5 : 2, pr: overlaps.length > 1 ? 1 : 2 }}>
        <Grid container>
          <Grid
            item
            xs={12}
            sm={overlaps.length > 1 ? 5 : 6}
            sx={{ pl: overlaps.length > 1 ? 1 : fullScreen ? 0 : 2, display: fullScreen ? "flex" : "block", justifyContent: "center" }}
          >
            <CardItem obj={newEntry} />
          </Grid>
          <Grid item xs={12} sm={overlaps.length > 1 ? 7 : 6} sx={{ pl: overlaps.length > 1 ? (fullScreen ? 1 : 4) : 0, mt: fullScreen ? 3 : 0 }}>
            <Grid container>
              <Grid item xs={1.25} sx={{ height: 205, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <IconButton
                  onClick={() => {
                    if (index > 0) {
                      if (fromList.length > 0) setSelected(overlaps[index - 1]!.id);
                      setIndex(index - 1);
                    }
                  }}
                  disabled={index === 0}
                  sx={{ py: 0, visibility: overlaps.length > 1 ? "visible" : "hidden" }}
                >
                  <KeyboardArrowLeftIcon sx={{ color: index !== 0 ? "text.primary" : "text.disabled", fontSize: 32 }} />
                </IconButton>
              </Grid>
              <Grid item xs={9.5} sx={{ display: "flex", justifyContent: "center" }}>
                <CardItem obj={overlaps[index] ? asEntry(overlaps[index]!) : undefined} />
              </Grid>
              <Grid item xs={1.25} sx={{ height: 205, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <IconButton
                  onClick={() => {
                    if (index < overlaps.length - 1) {
                      if (fromList.length > 0) setSelected(overlaps[index + 1]!.id);
                      setIndex(index + 1);
                    }
                  }}
                  disabled={index === overlaps.length - 1}
                  sx={{ py: 0, visibility: overlaps.length > 1 ? "visible" : "hidden" }}
                >
                  <KeyboardArrowRightIcon
                    sx={{ color: index !== overlaps.length - 1 ? "text.primary" : "text.disabled", fontSize: 32 }}
                  />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      {error ? (
        <Typography role="alert" sx={{ color: "error.main", fontSize: 14, px: 3, pt: 1 }}>
          {error}
        </Typography>
      ) : null}
      <DialogActions>
        <Button
          variant="contained"
          disabled={selected === undefined || busy}
          onClick={() => void manageClose()}
          sx={{
            backgroundColor: theme.palette.mode === "dark" ? "primary.dark" : "primary.main",
            textTransform: "none",
            borderRadius: 6,
            py: 1.1,
            my: 1,
            mr: 1,
            "&:hover": { backgroundColor: "primary.dark" },
            "&:disabled": { backgroundColor: "action.disabledBackground" },
          }}
        >
          <Typography fontSize={16}>{t("accept")}</Typography>
        </Button>
      </DialogActions>
    </Dialog>
  );
}
